<script>
import { mapGetters } from "vuex";
import pagination from "@/mixins/pagination";

import debounce from "debounce";
import helperService from "@/services/helper";
import patientService from "@/services/patient";
import userService from "@/services/user";
import inviteService from "@/services/invite";
import segmentService from "@/services/segment";
import {
  getMedicalInsuranceNumber,
  getMedicalInsurancePlan,
  getMedicalInsuranceName,
} from "@/services/patient-utils";
import {
  isProtocolsEnabled,
  isTasksEnabled,
  isPatientManagementRestricted,
  isManualPatientCreationRestricted,
  isPatientLockHiddenForDoctor,
} from "@/services/config";

import Spinner from "@/components/ui/Spinner";
import BaseSubNav from "@/components/ui/BaseSubNav.vue";

const DEBOUNCE_MS_TIMEOUT = 500;

export default {
  name: "PatientList",

  mixins: [pagination],

  components: {
    Spinner,
    BaseSubNav,
  },

  data() {
    return {
      isProtocolsEnabled,
      isTasksEnabled,
      isPatientLockHiddenForDoctor,
      isLoading: false,
      isLoadingDoctors: false,
      isLoadingManagers: false,
      searchInput: "",
      patients: [],
      doctors: [],
      managers: [],
      doctorFilter: null,
      // managerFilter: null,
    };
  },

  computed: {
    ...mapGetters(["isDoctor", "isManager", "isAdmin", "isAssistant", "user"]),

    arePatientActionsRestricted() {
      if (!isPatientManagementRestricted) return false;
      return (
        this.isAdmin || this.isDoctor || this.isAssistant || this.isManager
      );
    },

    canCreatePatient() {
      if (!isManualPatientCreationRestricted) return true;
      return (
        !this.isAdmin && !this.isDoctor && !this.isAssistant && !this.isManager
      );
    },
  },

  watch: {
    doctorFilter(newVal, oldVal) {
      if (newVal === oldVal) {
        return;
      }

      // if (!this.managerFilter) {
      //   const doctor = this.doctors.find((d) => d._id === newVal);
      //   if (doctor && doctor.supervisor) {
      //     this.managerFilter = doctor.supervisor;
      //   }
      // }

      this.getPatients();
    },

    // managerFilter(newVal, oldVal) {
    //   if (newVal === oldVal) {
    //     return;
    //   }

    //   if (this.doctorFilter) {
    //     const doctor = this.doctors.find((d) => d._id === this.doctorFilter);
    //     if (doctor && doctor.supervisor !== newVal) {
    //       this.doctorFilter = "";
    //     }
    //   }

    //   this.getPatients();
    //   this.getDoctors();
    // },
  },

  created() {
    this.getPatients();

    if (!this.isDoctor) {
      this.getDoctors();
    }

    if (!this.isDoctor && !this.isManager) {
      this.getManagers();
    }

    this.$bus.$on("patient-update", (patient) => {
      const index = this.patients.findIndex((p) => p._id === patient._id);
      if (index >= 0) {
        this.$set(this.patients, index, patient);
      } else {
        this.patients.unshift(patient);
      }
    });
  },

  methods: {
    hasPatientActions(patient) {
      if (!this.isDoctor && !this.arePatientActionsRestricted) return true;
      if (!this.arePatientActionsRestricted) return true;
      if (!(this.isPatientLockHiddenForDoctor && this.isDoctor)) return true;
      if (!patient.passwordSetAt && !this.arePatientActionsRestricted)
        return true;
      return false;
    },

    getDoctors() {
      this.isLoadingDoctors = true;

      userService
        .getDoctors()
        .then(({ docs }) => (this.doctors = docs))
        .finally(() => (this.isLoadingDoctors = false));
    },

    getManagers() {
      this.isLoadingManagers = true;

      userService
        .getManagers()
        .then(({ docs }) => (this.managers = docs))
        .finally(() => (this.isLoadingManagers = false));
    },

    buildSearchQuery() {
      const query = {};

      // Enviamos siempre el término como $text: el backend (buildSearchFilter)
      // resuelve si es nombre/apellido, documento o email de forma unificada.
      const searchInput = this.searchInput && this.searchInput.trim();
      if (searchInput) {
        query.$text = {
          $search: searchInput,
        };
      }

      return query;
    },

    getPatients(query = {}) {
      this.isLoading = true;

      const defaultQuery = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
        ...this.buildSearchQuery(),
        ...query,
      };

      defaultQuery.doctors = this.doctorFilter || undefined;

      patientService
        .get(defaultQuery)
        .then((res) => {
          this.patients = res.docs;
          this.pagination.total = res.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    debounceInput: debounce(function () {
      // Reset to the first page whenever a new search is performed.
      this.pagination.offset = 0;

      if (this.pagination.currentPage !== 1) {
        // Triggers the pagination watcher -> onPageChange -> getPatients.
        this.pagination.currentPage = 1;
      } else {
        this.getPatients();
      }
    }, DEBOUNCE_MS_TIMEOUT),

    getFullName(patient) {
      return helperService.getFullName(patient);
    },

    getMedicalInsuranceNumber,
    getMedicalInsurancePlan,
    getMedicalInsuranceName,

    getPatientDoctorsNames(doctors) {
      return helperService.getPatientDoctorsNames(doctors);
    },

    lockPatient(patient) {
      this.$confirm(
        `¿Desea bloquear a: ${helperService.getFullName(patient)}?`,
        "Bloquear",
        {
          confirmButtonText: "Aceptar",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      )
        .then(() => {
          segmentService.track({ name: "Patient Locked" });
          userService.lock(patient._id).then(() => {
            const index = this.patients.findIndex((p) => p._id === patient._id);
            this.$set(this.patients, index, {
              ...this.patients[index],
              lockedAt: new Date(),
            });
          });
        })
        .catch(() => {});
    },

    unlockPatient(id) {
      segmentService.track({ name: "Patient Unlocked" });
      userService.unlock(id).then(() => {
        const index = this.patients.findIndex((p) => p.user._id === id);
        const modifiedPatient = { ...this.patients[index] };
        delete modifiedPatient.lockedAt;
        this.$set(this.patients, index, modifiedPatient);
      });
    },

    reSendInvite(username) {
      inviteService
        .sendInvite(username)
        .then(() =>
          this.$message.success(`Se reenvío una invitación a ${username}`)
        );
    },

    onPageChange() {
      this.getPatients();
    },

    goToPatientDashboard(id) {
      helperService.callIfNoSelectedText(() =>
        this.$router.push({
          name: "patient-dashboard",
          params: { id },
        })
      );
    },

    getPatientStatus(user) {
      const { patient } = user;

      if (patient?.status === "closed") {
        return "Cerrado";
      }

      const lastEvent = patient?.lastEvent;
      if (!lastEvent) {
        return "Paciente nuevo";
      }

      const lastEventWasClosedStatus = lastEvent.status === "closed";
      const isActive = patient.status === "active";

      // This happens when the patient has an active episode but the last event
      // was a closed event from another episode.
      if (isActive && lastEventWasClosedStatus) {
        return "Paciente nuevo";
      }

      const name = lastEvent && lastEvent.form && lastEvent.form.name;
      return name;
    },
  },
};
</script>

<template lang="pug">
section.patients-list
  BaseSubNav
    template(#title)
      | Pacientes
      small(v-if="pagination.total")  ({{ pagination.total }})
    template(#actions)
      el-input.search__input(
        prefix-icon="el-icon-search"
        v-on:input="debounceInput"
        v-model="searchInput"
        placeholder="Buscar"
        :clearable="true"
      )
      router-link.button.button--blue(
        v-if="canCreatePatient"
        v-auth="'patient.create'"
        :to="{name: 'create-patient'}"
      )
        micon(name="add_circle_outline").button__icon
        span.button__text Nuevo Paciente
  .box
    .box__content--stretch
      table
        thead
          tr
            th Paciente
            th Seguro Salud
            th Plan
            th Nro Afiliado
            th Documento
            th Usuario
            th Edad
            th Última Atención
            //- th Número de Socio
            //- th(v-if="!isDoctor") Médico
            th Acciones
        tbody
          spinner(v-if="isLoading")
          tr(v-else v-for="(patient, index) in patients" :key="patient._id" @click="goToPatientDashboard(patient._id)")
            td
              b {{ getFullName(patient) }}
            td {{ getMedicalInsuranceName(patient) }}
            td {{ getMedicalInsurancePlan(patient) }}
            td {{ getMedicalInsuranceNumber(patient) }}
            td {{ patient.governmentId?.number || 'N/A' }}
            td {{ patient.username }}
            td {{ patient.bornAt | age }}
            td {{ patient.patient?.lastEvent?.createdAt | formatDate }}
            //- td {{ getLastAppointmentDate(patient) || 'Sin datos' }}        // Última Atención
            //- td(v-if="!isDoctor") {{ patient.doctors && patient.doctors.length ? getPatientDoctorsNames(patient.doctors) : 'Sin Médico' }}
            td.actions
              .actions-container
                el-dropdown(v-if="hasPatientActions(patient)" trigger="click")
                  .button.button--action.el-dropdown-link(@click.stop)
                    micon(name="more_horiz")
                  el-dropdown-menu(slot="dropdown")
                    el-dropdown-item(v-auth="'patient.update'" v-if="!isDoctor && !arePatientActionsRestricted")
                      router-link(:to="{ name: 'update-patient', params: { id: patient._id }}")
                        micon(name="edit")
                        | Editar paciente
                    el-dropdown-item(v-auth="'treatment.assign'" v-if="isProtocolsEnabled && !isDoctor && !arePatientActionsRestricted")
                      router-link(:to="{ name: 'assign-treatment-patient', params: { id: patient._id }}")
                        micon(name="note_add")
                        | Asignar Protocolo
                    el-dropdown-item(v-auth="'patient.access-private'" v-if="isTasksEnabled && !isDoctor && !arePatientActionsRestricted")
                      router-link(:to="{ name: 'patient-tasks', params: { id: patient._id }}")
                        micon(name="calendar")
                        | Ver tareas
                    el-dropdown-item(v-auth="'patient.access-private'" v-if="!arePatientActionsRestricted")
                      router-link(:to="{ name: 'patient-dashboard', params: { id: patient._id }}")
                        micon(name="insert_chart_outlined")
                        | Ver actividad
                    el-dropdown-item(v-if="!(isPatientLockHiddenForDoctor && isDoctor)" v-show="!patient.lockedAt" v-auth="'patient.update'")
                      div(@click="lockPatient(patient)")
                        micon(name="lock")
                        | Bloquear paciente
                    el-dropdown-item(v-if="!(isPatientLockHiddenForDoctor && isDoctor)" v-show="patient.lockedAt" v-auth="'patient.update'")
                      div(@click="unlockPatient(patient._id)")
                        micon(name="lock_open")
                        | Desbloquear paciente
                    el-dropdown-item(v-if="!patient.passwordSetAt && !arePatientActionsRestricted" v-auth="'patient.update'")
                      div(@click="reSendInvite(patient.username)")
                        micon(name="email")
                        | Reenviar invitación

    pagination(
      :isLoading="isLoading"
      :limit="pagination.limit"
      :total="pagination.total"
      @pagination="setPagination"
    )
</template>

<style lang="scss" scoped>
tbody td:hover {
  cursor: pointer;
}

small {
  margin-right: 5px;
  font-size: 10px;
  font-weight: 700;
}
</style>
